import { ApiClient } from "./apiClient";

const inFlightTableRequests =
  window.__makemakeInFlightTableRequests ||
  (window.__makemakeInFlightTableRequests = new Map());

const getRuntimeNumber = (key, buildTimeValue, fallback) => {
  const runtimeValue = window.__RUNTIME_CONFIG__?.[key];
  const parsed = Number(runtimeValue ?? buildTimeValue);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
};

export const DATA_RETRIEVAL_MAX_CONCURRENCY = Math.max(
  1,
  Math.floor(
    getRuntimeNumber(
      "PUBLIC__DATA_RETRIEVAL_MAX_CONCURRENCY",
      process.env.REACT_APP_DATA_RETRIEVAL_MAX_CONCURRENCY,
      4
    )
  )
);

const DATA_RETRIEVAL_RETRY_DELAY_MS = getRuntimeNumber(
  "PUBLIC__DATA_RETRIEVAL_RETRY_DELAY_MS",
  process.env.REACT_APP_DATA_RETRIEVAL_RETRY_DELAY_MS,
  500
);

const TRANSIENT_HTTP_STATUSES = new Set([502, 503, 504]);

export const createConcurrencyLimiter = (maxConcurrent) => {
  const limit = Math.max(1, Math.floor(Number(maxConcurrent) || 1));
  const queue = [];
  let activeCount = 0;

  const startOperation = ({ operation, resolve, reject }) => {
    activeCount += 1;

    Promise.resolve()
      .then(operation)
      .then(resolve, reject)
      .finally(() => {
        activeCount -= 1;
        runNext();
      });
  };

  const runNext = () => {
    while (activeCount < limit && queue.length > 0) {
      startOperation(queue.shift());
    }
  };

  return (operation) =>
    new Promise((resolve, reject) => {
      queue.push({ operation, resolve, reject });
      runNext();
    });
};

const scheduleDataRetrievalRequest = createConcurrencyLimiter(
  DATA_RETRIEVAL_MAX_CONCURRENCY
);

const wait = (delayMs) =>
  new Promise((resolve) => {
    window.setTimeout(resolve, delayMs);
  });

const getHttpStatus = (error) => error?.response?.status;

export const requestWithTransientRetry = async (
  operation,
  {
    schedule = (requestOperation) => requestOperation(),
    maxRetries = 1,
    retryDelayMs = DATA_RETRIEVAL_RETRY_DELAY_MS,
    random = Math.random,
  } = {}
) => {
  let attempt = 0;

  while (true) {
    try {
      return await schedule(operation);
    } catch (error) {
      const shouldRetry =
        attempt < maxRetries && TRANSIENT_HTTP_STATUSES.has(getHttpStatus(error));

      if (!shouldRetry) throw error;

      attempt += 1;
      const jitterMs = retryDelayMs > 0 ? Math.floor(random() * retryDelayMs * 0.25) : 0;
      await wait(retryDelayMs + jitterMs);
    }
  }
};

export function buildDRQuery(
  filterConfig = [],
  filterValues = {},
  aggregateBy = "",
  metricOp = "",
  valueColumn = ""
) {
  const params = new URLSearchParams();

  const pad2 = (n) => String(n).padStart(2, "0");

  const toLocalDate = (d) => {
    const yyyy = d.getFullYear();
    const mm = pad2(d.getMonth() + 1);
    const dd = pad2(d.getDate());
    return `${yyyy}-${mm}-${dd}`;
  };

  const norm = (v) => {
    if (v == null) return "";
    if (v instanceof Date) {
      return v.toISOString().slice(0, 19);
    }
    if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") return v;
    if (v.value !== undefined) return v.value;
    if (v.name  !== undefined) return v.name;
    if (v.label !== undefined) return v.label;
    if (v.selectedItem) {
      const si = v.selectedItem;
      return si.value ?? si.name ?? si.label ?? String(si);
    }
    return String(v);
  };

  const toISO = (x) => {
    if (!x) return "";
    if (x instanceof Date) {
      return x.toISOString().slice(0, 19);
    }
    if (/^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2})?$/.test(String(x))) return String(x);
    const d = new Date(x);
    if (!Number.isFinite(d.getTime())) return "";
    return d.toISOString().slice(0, 19);
  };

  const toDateOnly = (x) => {
    if (!x) return "";
    if (x instanceof Date) return toLocalDate(x);
    if (/^\d{4}-\d{2}-\d{2}$/.test(String(x))) return String(x);
    const d = new Date(x);
    if (!Number.isFinite(d.getTime())) return "";
    return toLocalDate(d);
  };

  for (const f of filterConfig) {
    const col = f.column;
    const raw = filterValues[f.id];

    if (!col) continue;


    if (f.type === "date_range") {
      const start = raw?.start ?? raw?.[0];
      const end   = raw?.end   ?? raw?.[1];

      const g = start instanceof Date ? toDateOnly(start) : toISO(start);
      const l = end instanceof Date ? toDateOnly(end) : toISO(end);

      if (g) params.append(`${col}__gte`, g);
      if (l) params.append(`${col}__lte`, l);
      continue;
    }

    if (f.type === "single_date") {
      const iso = raw instanceof Date ? toDateOnly(raw) : toISO(raw);
      if (iso) params.append(col, iso);  
      continue;
    }

    const val = norm(raw);

    const isNullish =
      val == null ||
      val === "" ||
      val === "all" ||
      (f.allow_null && f.null_label && val === f.null_label);

    if (isNullish) continue;

    if (Array.isArray(val)) {
      const cleaned = val.filter(
        (v) =>
          v != null &&
          v !== "" &&
          v !== "all" &&
          (!f.null_label || v !== f.null_label)
      );
      if (cleaned.length === 0) continue;
      params.append(col, String(cleaned[0]));
      continue;
    }

    params.append(col, String(val));
  }

  const gb = String(aggregateBy || "")
    .split(/[;,]/)
    .map((s) => s.trim())
    .filter(Boolean)
    .join(",");
  if (gb) params.set("aggregate", gb);

  const mop = String(metricOp || "").trim().toLowerCase();
  const valCol = String(valueColumn || "").trim();
  if (mop) {
    params.set("metrics", mop);
    if (valCol) {
      params.set("value", valCol);
    }
  }

  return params;
}


export const fetchTableRows = async (
  { tableId, filterConfig = [], filterValues = {}, aggregateBy = "", metricOp = "", valueColumn = "", extra = undefined },
  store
) => {

  const params = buildDRQuery(filterConfig, filterValues, aggregateBy, metricOp, valueColumn);
  
  if (extra?.panel_camera_id) {
    params.set("full_camera_id", String(extra.panel_camera_id));
  }
  if (extra?.fetch_last_only) {
    params.set("last_only", "date");
  }  

  if (Array.isArray(extra?.columns)) {
    extra.columns
      .filter(Boolean)
      .forEach((c) => params.append("columns", String(c)));
  }

  if (extra?.sample_limit != null) {
    params.set("sample_limit", String(extra.sample_limit));
  }

  const safeId = encodeURIComponent(String(tableId));
  const url = `/data-retrieval/table/${safeId}/?${params.toString()}`;

  if (inFlightTableRequests.has(url)) {
    return inFlightTableRequests.get(url);
  }

  const request = requestWithTransientRetry(
    () => ApiClient.dataRetrieval(store).get(url),
    { schedule: scheduleDataRetrievalRequest }
  )
    .finally(() => {
      inFlightTableRequests.delete(url);
    });

  inFlightTableRequests.set(url, request);
  return request;
};
